import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DEFAULT_PAGE_SIZE, type SortDirection } from '@/shared/api/api-types';

/**
 * Table state, stored in the URL.
 *
 * Pagination, sorting, search and filters live in search params rather than in
 * component state or a store, because all four must survive a refresh, work
 * with the browser's back button, and be shareable by pasting the URL to a
 * colleague (§8). "Send me the link to that failed order list" is a daily
 * operational need, and it only works if the view is in the address bar.
 *
 *   /orders?page=2&pageSize=25&search=ABC&status=failed&sort=createdAt&direction=desc
 *
 * Defaults are omitted from the URL to keep it readable — `/brands` and
 * `/brands?page=1&pageSize=25` describe the same view, so only the former is
 * ever produced.
 */

export interface TableSearchState {
  page: number;
  pageSize: number;
  search: string;
  sort: string | null;
  direction: SortDirection;
}

interface UseTableSearchParamsOptions {
  defaultPageSize?: number;
  defaultSort?: string;
  defaultDirection?: SortDirection;
  /**
   * Namespaces the table's own URL keys, for a second table on a page whose
   * other state already lives in the URL: `prefix: 'tx'` → `txPage`,
   * `txPageSize`, `txSearch`, `txSort`, `txDirection`. Keys passed to
   * `setFilter` are used as given. `clearFilters` and `hasActiveFilters` still
   * look at the whole URL (the page's state too), so a prefixed table should not
   * rely on them.
   */
  prefix?: string;
}

function tableKeys(prefix: string) {
  const key = (name: string) =>
    prefix ? `${prefix}${name.charAt(0).toUpperCase()}${name.slice(1)}` : name;
  return {
    page: key('page'),
    pageSize: key('pageSize'),
    search: key('search'),
    sort: key('sort'),
    direction: key('direction'),
  };
}

function parsePositiveInt(raw: string | null, fallback: number): number {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function parseDirection(raw: string | null, fallback: SortDirection): SortDirection {
  return raw === 'asc' || raw === 'desc' ? raw : fallback;
}

export function useTableSearchParams(options: UseTableSearchParamsOptions = {}) {
  const {
    defaultPageSize = DEFAULT_PAGE_SIZE,
    defaultSort = null,
    defaultDirection = 'desc',
    prefix = '',
  } = options;
  // Recomputed only when the prefix changes, so the callbacks below stay stable.
  const keys = useMemo(() => tableKeys(prefix), [prefix]);

  const [searchParams, setSearchParams] = useSearchParams();

  const state: TableSearchState = useMemo(
    () => ({
      page: parsePositiveInt(searchParams.get(keys.page), 1),
      pageSize: parsePositiveInt(searchParams.get(keys.pageSize), defaultPageSize),
      search: searchParams.get(keys.search) ?? '',
      sort: searchParams.get(keys.sort) ?? defaultSort,
      // Read both values explicitly: with an 'asc' default, treating anything but
      // 'asc' as the default would make a descending sort impossible.
      direction: parseDirection(searchParams.get(keys.direction), defaultDirection),
    }),
    [searchParams, keys, defaultPageSize, defaultSort, defaultDirection],
  );

  /**
   * Apply a patch to the URL.
   *
   * `null` removes a key. Everything funnels through here so that the
   * "omit defaults" and "replace, don't push" rules are applied consistently.
   */
  const patch = useCallback(
    (updates: Record<string, string | number | null | undefined>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);

          for (const [key, value] of Object.entries(updates)) {
            if (value === null || value === undefined || value === '') {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }

          if (next.get(keys.page) === '1') next.delete(keys.page);
          if (next.get(keys.pageSize) === String(defaultPageSize)) next.delete(keys.pageSize);

          return next;
        },
        // `replace` keeps the back button useful: it should return to the
        // previous *page*, not step back through every keystroke of a filter.
        { replace: true },
      );
    },
    [setSearchParams, keys, defaultPageSize],
  );

  const setPage = useCallback((page: number) => patch({ [keys.page]: page }), [patch, keys]);

  /**
   * Changing anything that alters the result set returns to page 1.
   *
   * Without this, filtering a 10-page list down to 2 pages while on page 7
   * shows an empty table and looks like a bug (§42).
   */
  const setFilter = useCallback(
    (key: string, value: string | number | null) => patch({ [key]: value, [keys.page]: null }),
    [patch, keys],
  );

  const setSearch = useCallback(
    (search: string) => setFilter(keys.search, search),
    [setFilter, keys],
  );

  const setPageSize = useCallback(
    (pageSize: number) => patch({ [keys.pageSize]: pageSize, [keys.page]: null }),
    [patch, keys],
  );

  /**
   * Set both sort keys in one update.
   *
   * These must be written together, in a single `patch`. Two successive
   * `setSearchParams` calls in the same event handler race: the second reads
   * the params as they were before the first committed, so whichever key is
   * written last wins and the other is dropped. The symptom is a URL like
   * `?direction=desc` with no `sort` at all, and a table that silently ignores
   * the click.
   */
  const setSorting = useCallback(
    (sort: string | null, direction: SortDirection | null) =>
      patch({ [keys.sort]: sort, [keys.direction]: direction, [keys.page]: null }),
    [patch, keys],
  );

  /** Third click on a sorted column clears sorting rather than cycling back. */
  const toggleSort = useCallback(
    (column: string) => {
      if (state.sort !== column) {
        setSorting(column, 'asc');
      } else if (state.direction === 'asc') {
        setSorting(column, 'desc');
      } else {
        setSorting(null, null);
      }
    },
    [setSorting, state.sort, state.direction],
  );

  const getFilter = useCallback((key: string) => searchParams.get(key) ?? null, [searchParams]);

  /** Reset every filter but keep the page size — that is a user preference. */
  const resetFilters = useCallback(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams();
        const pageSize = previous.get(keys.pageSize);
        if (pageSize) next.set(keys.pageSize, pageSize);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams, keys]);

  /**
   * Whether any filter is active, for the "Clear filters" affordance.
   * Pagination and sorting are not filters — they do not change which rows
   * match, only which are shown and in what order.
   */
  const hasActiveFilters = useMemo(() => {
    const ignored = new Set([keys.page, keys.pageSize, keys.sort, keys.direction]);
    for (const key of searchParams.keys()) {
      if (!ignored.has(key)) return true;
    }
    return false;
  }, [searchParams, keys]);

  return {
    ...state,
    setPage,
    setSorting,
    setPageSize,
    setSearch,
    setFilter,
    getFilter,
    toggleSort,
    resetFilters,
    hasActiveFilters,
  };
}
